import { Form } from 'react-bootstrap';
import Linkify from 'react-linkify';
import { CountrySelect } from '../common/CountrySelect';
import DropdownSelect from '../common/DropdownSelect';
import type {
  AnswerValue,
  DropdownSelectOption,
  Question,
} from '../../types/types';
import { isUndefined } from '../../util/util';
import { useEffect, useState } from 'react';
import FileAnswer from './FileAnswer';
import { getLocalizedString } from '../../util/getLocalizedString';
import { IndustrySelect } from '../common/IndustrySelect';
import NumberInput from '../common/NumberInput';

interface QuestionRendererProps {
  question: Question;
  value: AnswerValue;
  onChange: (val: AnswerValue) => void;
  questionIndex: number;
  error?: boolean;
  savingAnswer?: boolean;
  completed?: boolean;
}

export default function QuestionRenderer({
  question,
  value,
  onChange,
  questionIndex,
  error,
  savingAnswer,
  completed,
}: QuestionRendererProps) {
  const { type, label, required, help_text, id, key } = question;
  const invalidClass = error ? 'is-invalid' : '';
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    if (isUndefined(value)) return;
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (
      type === 'text' ||
      type === 'multiline' ||
      type === 'number' ||
      type === 'percentage'
    ) {
      const handler = setTimeout(() => {
        if (localValue !== value) {
          onChange(localValue);
        }
      }, 800);
      return () => clearTimeout(handler);
    }
  }, [localValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Linkify component decorator to open links in new tab
  const linkDecorator = (href: string, text: string, key: number) => (
    // TODO: the UX can be improved by replacing the full link text with an arbitrary text like "link" for longer urls
    <a href={href} key={key} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  );

  const renderInput = () => {
    switch (type) {
      case 'text':
        return (
          <Form.Control
            type="text"
            className={invalidClass}
            value={typeof localValue === 'string' ? localValue : ''}
            onChange={(e) => setLocalValue(e.target.value)}
            required={required}
            disabled={completed}
          />
        );

      case 'multiline':
        return (
          <Form.Control
            as="textarea"
            rows={3}
            className={invalidClass}
            value={typeof localValue === 'string' ? localValue : ''}
            onChange={(e) => setLocalValue(e.target.value)}
            required={required}
            disabled={completed}
          />
        );

      case 'number':
        return (
          <NumberInput
            value={typeof localValue === 'number' ? localValue : undefined}
            onChange={(val) => setLocalValue(val)}
            required={required}
            disabled={completed}
            className={invalidClass}
          />
        );
      // return (
      //   <Form.Control
      //     type="number"
      //     className={invalidClass}
      //     value={typeof localValue === 'number' ? localValue : ''}
      //     onChange={(e) => setLocalValue(Number(e.target.value))}
      //     required={required}
      //     disabled={completed}
      //   />
      // );

      case 'percentage':
        return (
          <>
            <Form.Range
              min={0}
              max={100}
              step={0.5}
              className={invalidClass}
              value={typeof localValue === 'number' ? localValue : 0}
              onChange={(e) => setLocalValue(Number(e.target.value))}
              required={required}
              disabled={completed}
            />
            <div className="text-center">{`${
              typeof localValue === 'number' ? localValue.toFixed(2) : '0.00'
            }%`}</div>
          </>
          // <Form.Control
          //   type="number"
          //   min={0}
          //   max={100}
          //   step={0.01}
          //   className={invalidClass}
          //   value={typeof localValue === 'number' ? localValue : ''}
          //   onChange={(e) => {
          //     let val = Number(e.target.value);
          //     if (val < 0) val = 0;
          //     if (val > 100) val = 100;
          //     setLocalValue(val);
          //   }}
          //   required={required}
          //   disabled={completed}
          // />
        );

      case 'date':
        return (
          <Form.Control
            type="date"
            className={invalidClass}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            disabled={savingAnswer || completed}
          />
        );

      case 'boolean':
        return (
          <>
            <Form.Check
              type="radio"
              label="No"
              className={invalidClass}
              name={`${question.id}`}
              checked={!isUndefined(value) && !(value as boolean)}
              id={`${question.id}-no`}
              onChange={(e) => onChange(!e.target.checked)}
              disabled={savingAnswer || completed}
            />
            <Form.Check
              type="radio"
              label="Yes"
              className={invalidClass}
              name={`${question.id}`}
              checked={value as boolean}
              id={`${question.id}-yes`}
              onChange={(e) => onChange(e.target.checked)}
              disabled={savingAnswer || completed}
            />
          </>
        );

      case 'single_choice': {
        // const options = Object.entries(question.options ?? {})
        //   .sort(([, a], [, b]) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
        //   .map((aa) => {
        //     return { value: aa[0], label: aa[1].label };
        //   });
        // .map(([value, { label }]) => ({ value, label }));

        const { options } = question;

        return (
          <Form.Select
            className={invalidClass}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={savingAnswer || completed}
          >
            <option value="">Select...</option>
            {options?.map((opt, i: number) => (
              <option key={i} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        );
      }

      case 'multi_choice': {
        // const options =
        //   question.options && typeof question.options === 'object'
        //     ? Object.entries(question.options).map(([value, label]) => ({
        //         value,
        //         label,
        //       }))
        //     : [];

        // const options = Object.entries(question.options ?? {})
        //   .sort(([, a], [, b]) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
        //   .map(([value, { label }]) => ({ value, label }));

        const { options } = question;

        if ((options?.length ?? 0) > 5) {
          return (
            <DropdownSelect
              options={
                options?.map((o) => {
                  return {
                    id: o.value,
                    label: o.label,
                  } as DropdownSelectOption;
                }) || []
              }
              selectedIds={typeof value === 'string' ? value.split(',') : []}
              // @ts-expect-error: ts(2322)
              onValueChanged={(selected: DropdownSelectOption[]) =>
                onChange(
                  selected
                    .map(
                      (selectedOption: DropdownSelectOption) =>
                        selectedOption.id,
                    )
                    .join(','),
                )
              }
              multiselect
              full
              disabled={savingAnswer || completed}
            />
          );
        } else {
          const valueChecked = (opt: string, includes: boolean) => {
            const originalValue = Array.isArray(value) ? value : [];
            const newValue = includes
              ? [...originalValue, opt]
              : originalValue.filter((v) => v !== opt);
            onChange(newValue);
          };
          return (
            <div>
              {options?.map((opt, i: number) => (
                <Form.Check
                  key={i}
                  type="checkbox"
                  className={invalidClass}
                  label={opt.label}
                  id={`${question.id}-${i}`}
                  checked={Array.isArray(value) && value.includes(opt.value)}
                  onChange={(e) => {
                    valueChecked(opt.value, e.target.checked);
                  }}
                  disabled={savingAnswer || completed}
                />
              ))}
            </div>
          );
        }
      }

      case 'file':
        return (
          <FileAnswer
            value={value as string}
            onChange={onChange}
            required={required}
            savingAnswer={savingAnswer || completed}
            error={error}
          />
        );

      case 'country':
        return (
          <CountrySelect full onChange={onChange} value={value as string} />
        );

      case 'industry':
        return (
          <IndustrySelect full onChange={onChange} value={value as string} />
        );

      default:
        return <p>Unsupported question type</p>;
    }
  };

  return (
    <Form.Group
      className={`p-3 border rounded bg-light ${error ? 'border-danger' : ''}`}
      data-testid={id}
      data-questionkey={key}
    >
      <Form.Label as="h3">
        {questionIndex + 1} - {label?.en}
      </Form.Label>
      {renderInput()}
      {getLocalizedString(help_text) && (
        <Form.Text className="text-muted">
          <Linkify componentDecorator={linkDecorator}>
            {getLocalizedString(help_text)}
          </Linkify>
        </Form.Text>
      )}
    </Form.Group>
  );
}
